import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Clock,
  Zap,
} from 'lucide-react';
import type { IntegrationHealth, IntegrationStatus } from '../../services/systemHealthService';
import { connectionMonitor } from '../../services/connectionMonitor';

const CHECKABLE_SERVICES = ['supabase', 'firebase'];

const SERVICE_LABELS: Record<string, string> = {
  supabase: 'Supabase',
  google_calendar: 'Google Calendar',
  openai: 'OpenAI',
  resend: 'Resend',
  firebase: 'Firebase',
  fathom: 'Fathom',
  grafana: 'Grafana',
  wasabi: 'Wasabi',
  qonto: 'Qonto',
};

function formatRelative(ts: string | null): string {
  if (!ts) return 'Nunca';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Ahora mismo';
  if (mins < 60) return `Hace ${mins}m`;
  if (hours < 24) return `Hace ${hours}h`;
  return `Hace ${days}d`;
}

interface StatusBadgeProps {
  status: IntegrationStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const configs = {
    healthy: {
      icon: <CheckCircle className="w-4 h-4" />,
      label: 'Operativo',
      classes: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    },
    degraded: {
      icon: <AlertTriangle className="w-4 h-4" />,
      label: 'Pendiente de verificar',
      classes: 'text-amber-700 bg-amber-50 border-amber-200',
    },
    failed: {
      icon: <XCircle className="w-4 h-4" />,
      label: 'Fallo',
      classes: 'text-red-700 bg-red-50 border-red-200',
    },
    unknown: {
      icon: <HelpCircle className="w-4 h-4" />,
      label: 'Sin datos',
      classes: 'text-gray-500 bg-gray-50 border-gray-200',
    },
  };

  const c = configs[status] ?? configs.unknown;

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${c.classes}`}>
      {c.icon}
      {c.label}
    </span>
  );
}

interface CardProps {
  health: IntegrationHealth;
  onCheck: (service: string) => void;
  checking: boolean;
}

function ServiceCard({ health, onCheck, checking }: CardProps) {
  const effectiveStatus = health.effective_status ?? health.status;
  const isCheckable = CHECKABLE_SERVICES.includes(health.service_name);
  const label = SERVICE_LABELS[health.service_name] ?? health.service_name;

  const borderColor = {
    healthy: 'border-emerald-100',
    degraded: 'border-amber-200',
    failed: 'border-red-200',
    unknown: 'border-gray-200',
  }[effectiveStatus];

  return (
    <div className={`bg-white rounded-xl border ${borderColor} p-4 flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-gray-800 text-sm">{label}</span>
        <StatusBadge status={effectiveStatus} />
      </div>

      <div className="space-y-1.5 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Comprobado: {formatRelative(health.last_check_at)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400" />
          <span>Exitoso: {formatRelative(health.last_success_at)}</span>
        </div>
        {health.response_time_ms != null && (
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 flex-shrink-0 text-blue-400" />
            <span>{health.response_time_ms}ms</span>
          </div>
        )}
      </div>

      {health.last_error_message && effectiveStatus !== 'healthy' && (
        <details className="text-xs text-red-600"><summary>No se pudo confirmar la conexión. Ver detalle técnico</summary><p className="break-words">{health.last_error_message}</p></details>
      )}

      {!isCheckable && <p className="text-xs text-gray-500">Sin comprobación manual disponible desde este panel.</p>}
      {isCheckable && (
        <button
          onClick={() => onCheck(health.service_name)}
          disabled={checking}
          className="mt-auto flex items-center justify-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg py-1.5 px-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} />
          Comprobar ahora
        </button>
      )}
    </div>
  );
}

function EmptyServiceCard({ service }: { service: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3 opacity-60">
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-gray-700 text-sm">
          {SERVICE_LABELS[service] ?? service}
        </span>
        <StatusBadge status="unknown" />
      </div>
      <p className="text-xs text-gray-400">Sin comprobaciones registradas</p>
    </div>
  );
}

interface Props {
  integrations: IntegrationHealth[];
  onRefresh: () => void | Promise<void>;
}

const KNOWN_SERVICES = [
  'supabase', 'google_calendar', 'openai',
  'resend', 'firebase', 'fathom', 'grafana',
];

const IntegrationHealthPanel: React.FC<Props> = ({ integrations, onRefresh }) => {
  const [checkingService, setCheckingService] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const handleCheck = async (service: string) => {
    setCheckingService(service);
    try {
      await connectionMonitor.checkService(service);
      await onRefresh();
      setToast(`Comprobaci\u00f3n de ${SERVICE_LABELS[service] ?? service} completada`);
    } catch {
      await onRefresh();
      setToast(`No se pudo confirmar la conexión con ${SERVICE_LABELS[service] ?? service}`);
    } finally {
      setCheckingService(null);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const healthMap = new Map(integrations.map((i) => [i.service_name, i]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Estado de Integraciones</h2>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...new Set([...KNOWN_SERVICES, ...healthMap.keys()])].map((service) => {
          const health = healthMap.get(service);
          if (!health) return <EmptyServiceCard key={service} service={service} />;
          return (
            <ServiceCard
              key={service}
              health={health}
              onCheck={handleCheck}
              checking={checkingService === service}
            />
          );
        })}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg z-50 animate-in fade-in">
          {toast}
        </div>
      )}
    </div>
  );
};

export default IntegrationHealthPanel;
